// Kit 03 · items 58, 59, 60. The layers that do NOT take the page away.
//
//   58 Popover      width 280 · pad 16 · radius 12 · offset 8 from anchor
//   59 Tooltip      pad 8 by 12 · radius 6 · max width 240 · offset 8
//   60 Context menu width 212 · row 34 · radius 12 · pad 4
//
// None of the three registers with OverlayProvider, and that is deliberate:
// the provider owns the scrim, and a popover has no scrim. Item 58 says so in
// as many words — "Anchor only. No scrim." — and item 59 has no scrim either.
// Registering them would dim the page for a tooltip.
//
// They still owe the rest of the dismiss contract, which is what this file is:
// Escape closes, click outside dismisses, focus returns to the anchor.

import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { Icon } from "./icon";
import type { LucideIcon } from "lucide-react";
import { cx, stateAttr } from "./kit-state";
import { useEscape } from "./overlay";
import type { KitState, Stateful } from "./kit-state";

/**
 * Click outside dismisses. Item 58's contract, and item 60 inherits it from
 * every menu ever built.
 *
 * Pointerdown rather than click: a click fires after the mouse comes back up,
 * so a drag that starts inside the panel and ends outside it would close the
 * panel the user was dragging in.
 */
export function useDismissOnOutside(
  refs: Array<RefObject<HTMLElement | null>>,
  active: boolean,
  onDismiss?: () => void,
) {
  useEffect(() => {
    if (!active || !onDismiss) return;
    function onDown(event: PointerEvent) {
      const target = event.target as Node;
      if (refs.some((r) => r.current?.contains(target))) return;
      onDismiss?.();
    }
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, onDismiss, ...refs]);
}

/**
 * Focus goes back to the anchor when a non-trapping layer closes.
 *
 * Exported because the calendar needs it and lives in its own file. It was not
 * exported first, and the calendar therefore did not call it: Escape closed the
 * panel and left focus on <body>, which looks identical to working in every
 * screenshot and is the difference between a keyboard user carrying on and a
 * keyboard user starting again from the top of the page.
 */
export function useReturnToAnchor(active: boolean, anchor?: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!active || !anchor) return;
    return () => {
      const back = anchor.current;
      if (back && document.contains(back)) back.focus();
    };
  }, [active, anchor]);
}

/* ═══ 58 · popover ══════════════════════════════════════════════════════════
   keys: Escape closes and returns focus to the anchor. Click outside
   dismisses. Focus is NOT trapped.
   ═════════════════════════════════════════════════════════════════════════ */

export interface PopoverProps extends Stateful {
  open: boolean;
  onClose?: () => void;
  /** The heading line. Item 58 draws "Response window". */
  title?: string;
  children: ReactNode;
  /** The control this hangs off. Focus returns here and a click on it is not
      treated as a click outside. */
  anchor?: RefObject<HTMLElement | null>;
  className?: string;
}

export function Popover({ open, onClose, title, children, anchor, state, className }: PopoverProps) {
  const panel = useRef<HTMLDivElement | null>(null);
  const live = open || state === "open";

  useEscape(live, onClose);
  useDismissOnOutside(anchor ? [panel, anchor] : [panel], live, onClose);
  useReturnToAnchor(live, anchor);

  if (!live) return null;

  return (
    <div ref={panel} role="dialog" aria-label={title} className={cx("k-popover", className)} {...stateAttr(state)}>
      {title ? <span className="k-popover__title">{title}</span> : null}
      <div className="k-popover__body">{children}</div>
    </div>
  );
}

/* ═══ 59 · tooltip ══════════════════════════════════════════════════════════
   keys: Appears on hover AND on keyboard focus. Escape dismisses. Never holds
   interactive content.

   "Never holds interactive content" is enforced rather than documented: the
   bubble is a <span> with pointer-events off, so a control placed inside it
   could not be clicked even if someone put one there. A rule a component can
   break is a rule stated in prose and checked never.
   ═════════════════════════════════════════════════════════════════════════ */

export interface TooltipProps extends Stateful {
  /** The sentence. A string, not a node, because nothing interactive fits. */
  label: string;
  children: ReactNode;
  className?: string;
}

export function Tooltip({ label, children, state, className }: TooltipProps) {
  const [shown, setShown] = useState(false);
  const id = useId();
  const live = shown || state === "open";

  useEscape(live, () => setShown(false));

  return (
    <span
      className={cx("k-tip-anchor", className)}
      onPointerEnter={() => setShown(true)}
      onPointerLeave={() => setShown(false)}
      onFocus={() => setShown(true)}
      onBlur={() => setShown(false)}
    >
      <span aria-describedby={live ? id : undefined}>{children}</span>
      {live ? (
        <span role="tooltip" id={id} className="k-tip" {...stateAttr(state)}>
          {label}
        </span>
      ) : null}
    </span>
  );
}

/* ═══ 60 · context menu ═════════════════════════════════════════════════════
   redline: width 212 · row 34 · radius 12 · pad 4
   keys: Arrow keys move. Enter selects. Escape closes and returns focus to the
   trigger. "The menu holds focus and the active row is tinted, so no row
   carries its own ring."

   That last clause is the design of the whole component. A menu whose rows are
   each focusable puts a 2px ring inside a 212px box on every arrow press, and
   the ring is then the loudest thing in a list of five quiet rows. So the
   CONTAINER is the tab stop, the active row is named by aria-activedescendant,
   and the tint is the only indicator. Which means the tint has to be readable
   on its own: it is --a-brand-sub, the same wash the specimen draws, and not
   --s-hover, or the active row and a hovered row would be indistinguishable.
   ═════════════════════════════════════════════════════════════════════════ */

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
  onSelect?: () => void;
  /** Forced, for the gallery. */
  state?: KitState;
}

export interface ContextMenuProps extends Stateful {
  label: string;
  items: ContextMenuItem[];
  onClose?: () => void;
  /** Focus returns here on Escape. */
  anchor?: RefObject<HTMLElement | null>;
  className?: string;
}

export function ContextMenu({ label, items, onClose, anchor, state, className }: ContextMenuProps) {
  const root = useRef<HTMLDivElement | null>(null);
  const baseId = useId();
  const enabled = items.filter((i) => !i.disabled);
  const [activeId, setActiveId] = useState<string | undefined>(enabled[0]?.id);

  useEscape(true, onClose);
  useDismissOnOutside(anchor ? [root, anchor] : [root], Boolean(onClose), onClose);
  useReturnToAnchor(Boolean(onClose), anchor);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      const item = items.find((i) => i.id === activeId);
      if (item && !item.disabled) { event.preventDefault(); item.onSelect?.(); }
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    if (enabled.length === 0) return;
    const at = enabled.findIndex((i) => i.id === activeId);
    const step = event.key === "ArrowDown" ? 1 : -1;
    setActiveId(enabled[(at + step + enabled.length) % enabled.length].id);
  }

  return (
    <div
      ref={root}
      role="menu"
      aria-label={label}
      aria-activedescendant={activeId ? `${baseId}-${activeId}` : undefined}
      tabIndex={0}
      className={cx("k-menu", "k-menu--context", className)}
      onKeyDown={onKeyDown}
      {...stateAttr(state)}
    >
      {items.map((item) => (
        <div
          key={item.id}
          id={`${baseId}-${item.id}`}
          role="menuitem"
          aria-disabled={item.disabled || undefined}
          className="k-menu__row"
          // The active row is not focused, so it cannot use :focus-visible.
          // data-state is the same attribute every other kit component uses for
          // a state the browser will not give it, and kit.css matches both in
          // one rule.
          {...stateAttr(item.state ?? (item.id === activeId ? "focus" : undefined))}
          onPointerDown={item.disabled ? undefined : item.onSelect}
        >
          {item.icon ? <Icon icon={item.icon} size="sm" /> : null}
          <span className="k-menu__label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}
